import type MP4Box from 'mp4box';
import { MP4_VIDEO_CODEC, MP4_VIDEO_HANDLER } from '@studio/constants/library';
import type { Library } from '@studio/types';
import type { AddError } from '../../types/upload-video';
import { VALIDATIONS } from '../../types/upload-video';

const _isSupportedHevcPixelFormat = (hvcC?: Library.Derived.ParsedHvcCBox): boolean => {
  if (hvcC === undefined) {
    return false;
  }

  return hvcC.chroma_format_idc === 1 && hvcC.bit_depth_luma_minus8 === 0 && hvcC.bit_depth_chroma_minus8 === 0;
};

const _isSupportedAv1PixelFormat = (av1C?: Library.Derived.ParsedAv1CBox): boolean => {
  if (av1C === undefined) {
    return false;
  }

  return av1C.high_bitdepth === 0 && av1C.twelve_bit === 0;
};

const _getVideoSampleEntry = (file: MP4Box.MP4File): Library.Derived.ParsedStsdEntry | undefined => {
  const videoTrack = (file.moov?.traks as Library.Derived.ParsedTrack[] | undefined)?.find(
    (trak) => trak.mdia?.hdlr?.handler === MP4_VIDEO_HANDLER
  );

  return videoTrack?.mdia?.minf?.stbl?.stsd?.entries?.find((entry) => {
    return (
      entry.type === MP4_VIDEO_CODEC.AVC1 ||
      entry.type === MP4_VIDEO_CODEC.AV01 ||
      entry.type === MP4_VIDEO_CODEC.HEV1 ||
      entry.type === MP4_VIDEO_CODEC.HVC1
    );
  });
};

const validatePixelFormat = (file: MP4Box.MP4File, addError: AddError): void => {
  const sampleEntry = _getVideoSampleEntry(file);

  if (sampleEntry === undefined) {
    return;
  }

  if (sampleEntry.type === MP4_VIDEO_CODEC.AVC1) {
    return;
  }

  if (sampleEntry.type === MP4_VIDEO_CODEC.HEV1 || sampleEntry.type === MP4_VIDEO_CODEC.HVC1) {
    if (!_isSupportedHevcPixelFormat(sampleEntry.hvcC)) {
      addError(VALIDATIONS.INVALID_PIXEL_FORMAT);
    }

    return;
  }

  if (sampleEntry.type === MP4_VIDEO_CODEC.AV01 && !_isSupportedAv1PixelFormat(sampleEntry.av1C)) {
    addError(VALIDATIONS.INVALID_PIXEL_FORMAT);
  }
};

export default validatePixelFormat;
